// @flow

import { createStore, applyMiddleware, compose, combineReducers } from 'redux';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import crosstabSync from './crossTab';
import { routerMiddleware, connectRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import rootReducerConfig from '../reducers';
import initialState from './initialState';
import middleware from '../middleware';
import { shouldLog } from '../lib/helpers';

export const history: History = createBrowserHistory();

const enhancers = [];

if (typeof window.__REDUX_DEVTOOLS_EXTENSION__ === 'function')
  enhancers.push(
    window.__REDUX_DEVTOOLS_EXTENSION__({ serialize: true, trace: true })
  );

if (shouldLog) middleware.push(require('logrocket').reduxMiddleware());

const composedEnhancers = compose(
  applyMiddleware(routerMiddleware(history), ...middleware),
  ...enhancers
);

const createReducerFromConfig = ({
  name,
  reducers,
  persist,
}: reducerConfigType) => {
  const reducer = (state = initialState[name], action) =>
    !reducers[action.type]
      ? state
      : reducers[action.type]({ ...state }, action);
  return persist ? persistReducer({ storage, ...persist }, reducer) : reducer;
};

const createRootReducer = (config: Array<reducerConfigType>) => {
  const rootReducer = { router: connectRouter(history) };
  config.forEach(
    reducerConfig =>
      (rootReducer[reducerConfig.name] = createReducerFromConfig(reducerConfig))
  );
  return rootReducer;
};

const rootReducer = createRootReducer(rootReducerConfig);

// Reference:
// https://medium.com/front-end-weekly/code-splitting-redux-reducers-4073db30c72e
// https://engineering.innovid.com/code-splitting-using-lazy-loading-with-react-redux-typescript-and-webpack-4-3ec60140ec5a
const combineLazyReducers = reducers => {
  let reducerKeys = new Set(Object.keys(reducers));

  Object.keys(initialState)
    .filter(key => !reducerKeys.has(key))
    .forEach(key => (reducers[key] = state => state || null));

  return combineReducers(reducers);
};

const store: Store<any, any> = createStore(
  combineLazyReducers(rootReducer),
  initialState,
  composedEnhancers
);

const asyncReducers = {};

export const loadLazyReducer = async (importReducer: Function) => {
  if (!store) return Promise.reject();
  const { default: reducerConfig } = await importReducer();

  if (!reducerConfig || !reducerConfig.name) return Promise.reject();

  asyncReducers[reducerConfig.name] = createReducerFromConfig(reducerConfig);
  store.replaceReducer(
    combineLazyReducers({ ...rootReducer, ...asyncReducers })
  );

  return Promise.resolve();
};

crosstabSync(store, storage, [
  {
    key: 'session',
    whitelist: ['data'],
    dataCheck: (oldState, newState) =>
      oldState.data &&
      oldState.data.maxIdleExpirationTime &&
      newState.data &&
      newState.data.maxIdleExpirationTime &&
      newState.data.maxIdleExpirationTime < oldState.data.maxIdleExpirationTime,
  },
  {
    key: 'auth',
    whitelist: ['isAuthenticated'],
    dataCheck: (oldState, newState) =>
      oldState.isAuthenticated === newState.isAuthenticated,
  },
]);

const getStoreAndPersistor = () => ({
  store,
  persistor: persistStore(store),
});

export default getStoreAndPersistor;
